import { createStore } from 'vuex'
import api from '../services/api'
import navigation from './modules/navigation'
import location from './modules/location'
import pointPreview from './modules/pointPreview'
import modal from './modules/modal'
import overlay from './modules/overlay'
import game from './modules/game'
import modules from './modules/modules'

// Settings module (mirrors SettingsContext)
const settings = {
  namespaced: true,
  state: {
    tourSettings: null,
    loading: false,
    error: null
  },
  mutations: {
    SET_TOUR_SETTINGS(state, settings) {
      state.tourSettings = settings
      state.error = null
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ERROR(state, error) {
      state.error = error
      state.loading = false
    }
  },
  actions: {
    async loadTourSettings({ commit, dispatch, state }, activeTour) {
      // Prevent multiple simultaneous calls
      if (state.loading) {
        console.log('Settings already loading, skipping duplicate call')
        return state.tourSettings
      }
      
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      try {
        console.log('=== LOADING TOUR SETTINGS ===')
        console.log('Active tour:', activeTour)
        console.log('API URL:', process.env.CLIENT_API_URL + '/client/settings')
        
        const response = await api.getTourSettings(activeTour)
        console.log('API response:', response)
        console.log('API response data:', response.data)
        console.log('API response data keys:', Object.keys(response.data || {}))
        
        // Preserve API-provided id; fall back to activeTour only if missing
        const settingsWithTourId = {
          ...response.data,
          id: response.data?.id ?? activeTour
        }
        
        console.log('Settings with tour ID:', settingsWithTourId)
        console.log('Settings tour ID:', settingsWithTourId.id)
        
        commit('SET_TOUR_SETTINGS', settingsWithTourId)
        
        // Initialize point navigation when settings are loaded
        if (response.data?.navigation) {
          dispatch('pointNavigation/initializeNavigation', response.data, { root: true })
        }
        
        // If settings are from a tour, set active tourId (mirrors React Layout.js behavior)
        if (response.data.id) {
          dispatch('tours/setActiveTour', response.data.id, { root: true })
        }
        
        return settingsWithTourId
      } catch (error) {
        console.error('Error loading tour settings:', error)
        
        // Handle 404 errors by setting error state instead of redirecting
        if (error.response && error.response.status === 404) {
          commit('SET_ERROR', { type: 'not_found', message: 'Tour not found' })
          return null
        }
        
        // Handle other errors
        commit('SET_ERROR', { type: 'error', message: error.message || 'Failed to load settings' })
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // Helper action to load tour settings with automatic tour ID detection
    async loadTourSettingsAuto({ dispatch }, { i18n, params }) {
      console.log('=== SETTINGS MODULE - LOAD TOUR SETTINGS AUTO (PARAMS ONLY) ===')
      const tourId = params?.tourId || null
      return await dispatch('loadTourSettings', tourId)
    }
  },
  getters: {
    tourSettings: state => state.tourSettings,
    loading: state => state.loading,
    error: state => state.error
  }
}

// Tours module (mirrors ToursContext)
const tours = {
  namespaced: true,
  state: {
    tours: [],
    activeTour: localStorage.getItem('activeTour') || null,
    loading: false
  },
  mutations: {
    SET_TOURS(state, tours) {
      state.tours = tours
    },
    SET_ACTIVE_TOUR(state, tourId) {
      state.activeTour = tourId
      localStorage.setItem('activeTour', tourId)
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    }
  },
  actions: {
    async loadTours({ commit }, activeTour) {
      commit('SET_LOADING', true)
      try {
          console.log("store LoadTours getTourPoints", activeTour);
        const response = await api.getTourPoints(activeTour)
        commit('SET_TOURS', response.data || [])
        return response.data
      } catch (error) {
        console.error('Error loading tours:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async loadPoint({ commit }, { pointId, activeTour }) {
      try {
        const response = await api.getPoint(pointId, activeTour)
        return response
      } catch (error) {
        console.error('Error loading point:', error)
        throw error
      }
    },
    async loadTourPoint({ commit }, { tourPointId, activeTour, language }) {
      try {
        console.log('=== LOAD TOUR POINT ACTION ===')
        console.log('tourPointId:', tourPointId)
        console.log('activeTour:', activeTour)
        console.log('language:', language)
        
        const response = await api.getTourPoint(tourPointId, activeTour, language)
        console.log('Tour point API response:', response)
        return response
      } catch (error) {
        console.error('Error loading tour point:', error)
        throw error
      }
    },
    setActiveTour({ commit }, tourId) {
      commit('SET_ACTIVE_TOUR', tourId)
    },
    resetActiveTour({ commit }) {
      commit('SET_ACTIVE_TOUR', null)
      localStorage.removeItem('activeTour')
    },
    initializeFromStorage({ commit }) {
      const storedTour = localStorage.getItem('activeTour')
      if (storedTour) {
        commit('SET_ACTIVE_TOUR', storedTour)
      }
    }
  },
  getters: {
    tours: state => state.tours,
    activeTour: state => state.activeTour,
    loading: state => state.loading
  }
}

// Visited module (mirrors VisitedContext)
const visited = {
  namespaced: true,
  state: {
    infoPageVisited: localStorage.getItem('infoPageVisited') === 'true',
    visitedPoints: JSON.parse(localStorage.getItem('visitedPoints') || '[]'),
    startPointVisited: JSON.parse(sessionStorage.getItem('startPointVisited') || '{}')
  },
  mutations: {
    SET_INFO_PAGE_VISITED(state, visited) {
      state.infoPageVisited = visited
      localStorage.setItem('infoPageVisited', visited)
    },
    SET_START_POINT_VISITED(state, visited) {
      state.startPointVisited = visited
      sessionStorage.setItem('startPointVisited', JSON.stringify(visited))
    },
    SET_VISITED_POINTS(state, points) {
      state.visitedPoints = points
      localStorage.setItem('visitedPoints', JSON.stringify(points))
    },
    ADD_VISITED_POINT(state, pointId) {
      if (!state.visitedPoints.includes(pointId)) {
        state.visitedPoints.push(pointId)
        localStorage.setItem('visitedPoints', JSON.stringify(state.visitedPoints))
      }
    }
  },
  actions: {
    setStartPointVisited({ commit }, visited) {
      commit('SET_START_POINT_VISITED', visited)
    },
    setInfoPageVisited({ commit }, visited) {
      commit('SET_INFO_PAGE_VISITED', visited)
    },
    addVisitedPoint({ commit }, pointId) {
      commit('ADD_VISITED_POINT', pointId)
    },
    initializeFromStorage({ commit }) {
      const infoPageVisited = localStorage.getItem('infoPageVisited') === 'true'
      const visitedPoints = JSON.parse(localStorage.getItem('visitedPoints') || '[]')
      const startPointVisited = JSON.parse(sessionStorage.getItem('startPointVisited') || '{}')
      commit('SET_VISITED_POINTS', visitedPoints)
      commit('SET_START_POINT_VISITED', startPointVisited)
    }
  },
  getters: {
    startPointVisited: state => state.startPointVisited,
    infoPageVisited: state => state.infoPageVisited,
    visitedPoints: state => state.visitedPoints
  }
}

// Point navigation module (mirrors PointContext)
const pointNavigation = {
  namespaced: true,
  state: {
    unlocked: JSON.parse(sessionStorage.getItem('points') || '{"unlocked": {}}').unlocked || {},
    pointNavigation: null,
    showPointNavigation: true
  },
  mutations: {
    SET_UNLOCKED_POINTS(state, unlocked) {
      state.unlocked = unlocked
      sessionStorage.setItem('points', JSON.stringify({ unlocked }))
    },
    SET_POINT_NAVIGATION(state, navigation) {
      state.pointNavigation = navigation
      console.log('Vuex: Setting point navigation to:', navigation)
    },
    SET_SHOW_POINT_NAVIGATION(state, show) {
      state.showPointNavigation = show
    }
  },
  actions: {
    setUnlockedPoints({ commit }, unlocked) {
      commit('SET_UNLOCKED_POINTS', unlocked)
    },
    switchPointNavigation({ commit }, navigation) {
      commit('SET_POINT_NAVIGATION', navigation)
      console.log('Vuex: Point navigation switched to:', navigation)
    },
    setShowPointNavigation({ commit }, show) {
      commit('SET_SHOW_POINT_NAVIGATION', show)
    },
    // Initialize navigation from settings
    initializeNavigation({ commit, state }, settings) {
      if (!settings?.navigation || state.pointNavigation) return
      
      console.log('Vuex: Initializing navigation from settings:', settings.navigation)
      
      // Set the default point navigation based on the tour settings
      if (settings.navigation.google_maps?.active || settings.navigation.open_streetmap?.active) {
        commit('SET_POINT_NAVIGATION', 'outdoor_map')
      } else if (settings.navigation.map?.active) {
        commit('SET_POINT_NAVIGATION', 'map')
      } else if (settings.navigation.list?.active) {
        commit('SET_POINT_NAVIGATION', 'list')
      } else if (settings.navigation.qr_code?.active) {
        commit('SET_POINT_NAVIGATION', 'qr_code')
      }
      
      // Special case for QR code on specific hostnames
      if (settings.navigation.qr_code?.active && 
          (window.location.hostname === "mas.tourio.com" || window.location.hostname === "mas.dev.tourio.com")) {
        commit('SET_POINT_NAVIGATION', 'qr_code')
      }
    }
  },
  getters: {
    unlocked: state => state.unlocked,
    pointNavigation: state => state.pointNavigation,
    showPointNavigation: state => state.showPointNavigation
  }
}

// Create the store
const store = createStore({
  modules: {
    settings,
    tours,
    visited,
    pointNavigation,
    navigation,
    location,
    pointPreview,
    modal,
    overlay,
    game,
    modules
  }
})

// Restore persisted module states as early as possible
try {
  store.dispatch('modules/initializeFromStorage')
} catch (e) {
  // ignore
}

export default store 